import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FileText, Download, BookOpen, Mic, Lock, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NOORANI_PDF = "https://customer-assets.emergentagent.com/job_8750a84e-c22d-4477-a56b-9b9bbdfad8f2/artifacts/qy1m14km_Noorani-Qaidah-2000-by-Muhammad-ar-Rai.pdf";
const TAJWEED_PDF = "https://customer-assets.emergentagent.com/job_8750a84e-c22d-4477-a56b-9b9bbdfad8f2/artifacts/cw48mch8_%D8%A7%D9%84%D8%AA%D8%AC%D9%88%D9%8A%D8%AF%20%D8%A7%D9%84%D9%85%D8%B5%D9%88%D8%B1-.pdf";

const materials = {
  sv: [
    {
      title: "Quran Online Method",
      subtitle: "Lär dig läsa Koranen från grunden",
      description: "En komplett nybörjarguide för att lära sig läsa Koranen med korrekt uttal. Boken tar dig steg för steg genom det arabiska alfabetet, vokaler, och grundläggande Tajweed-regler. Skriven av Shaykh Noor Mohammad ar-Ra'ee.",
      language: "Engelska & Arabiska",
      level: "Nybörjare",
      pages: "36 sidor",
      topics: "Arabiska alfabetet, Vokaler, Tajweed-grunder, Artikulationspunkter",
      icon: BookOpen,
      url: NOORANI_PDF,
    },
    {
      title: "Al-Tajweed Al-Musawwar",
      subtitle: "Illustrerad Tajweed-guide av Dr. Ayman Suweid",
      description: "En detaljerad och illustrerad guide till Tajweed-vetenskapen. Innehåller anatomiska diagram för artikulationspunkter, detaljerade regler för korrekt koranrecitation, Nun Sakinah, Meem Sakinah, Madd-regler och praktiska övningar.",
      language: "Arabiska",
      level: "Alla nivåer",
      pages: "Omfattande",
      topics: "Artikulationspunkter, Nun & Meem Sakinah, Madd, Idghaam, Ikhfaa",
      icon: Mic,
      url: TAJWEED_PDF,
    },
  ],
  en: [
    {
      title: "Quran Online Method",
      subtitle: "Master reading the Quran from scratch",
      description: "A complete beginner's guide to learning to read the Quran with correct pronunciation. The book takes you step by step through the Arabic alphabet, vowels, and fundamental Tajweed rules. Written by Shaykh Noor Mohammad ar-Ra'ee.",
      language: "English & Arabic",
      level: "Beginner",
      pages: "36 pages",
      topics: "Arabic Alphabet, Vowels, Tajweed Basics, Articulation Points",
      icon: BookOpen,
      url: NOORANI_PDF,
    },
    {
      title: "Al-Tajweed Al-Musawwar",
      subtitle: "Illustrated Tajweed Guide by Dr. Ayman Suweid",
      description: "A detailed and illustrated guide to the science of Tajweed. Contains anatomical diagrams for articulation points, detailed rules for correct Quran recitation, Nun Sakinah, Meem Sakinah, Madd rules and practical exercises.",
      language: "Arabic",
      level: "All levels",
      pages: "Comprehensive",
      topics: "Articulation Points, Nun & Meem Sakinah, Madd, Idghaam, Ikhfaa",
      icon: Mic,
      url: TAJWEED_PDF,
    },
  ],
};

const texts = {
  sv: {
    title: "Lärmaterial",
    subtitle: "Exklusivt material för våra elever",
    loginTitle: "Elevportal",
    loginSubtitle: "Ange din elevkod för att komma åt lärmaterialet",
    passwordLabel: "Elevkod",
    passwordPlaceholder: "Ange din elevkod...",
    loginButton: "Logga in",
    wrongPassword: "Fel elevkod. Kontakta Ustadh Ismail för att få din kod.",
    openPdf: "Öppna PDF",
    logout: "Logga ut",
    topics: "Ämnen",
    info: "Dessa material används i våra lektioner. Kontakta Ustadh Ismail om du har frågor om materialet.",
  },
  en: {
    title: "Learning Materials",
    subtitle: "Exclusive materials for our students",
    loginTitle: "Student Portal",
    loginSubtitle: "Enter your student code to access learning materials",
    passwordLabel: "Student Code",
    passwordPlaceholder: "Enter your student code...",
    loginButton: "Log In",
    wrongPassword: "Wrong student code. Contact Ustadh Ismail to get your code.",
    openPdf: "Open PDF",
    logout: "Log Out",
    topics: "Topics",
    info: "These materials are used in our lessons. Contact Ustadh Ismail if you have questions about the material.",
  },
};

const MaterialsPage = () => {
  const { language } = useLanguage();
  const items = materials[language] || materials.sv;
  const t = texts[language] || texts.sv;

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('quran-student-auth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API_URL}/api/materials/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: password }),
      });
      if (res.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('quran-student-auth', 'true');
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('quran-student-auth');
    setPassword('');
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="materials-page">
      <section className="py-16 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: 'var(--text-primary)' }} data-testid="materials-title">
            {t.title}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
      </section>

      <div className="gold-divider"></div>

      {!isAuthenticated ? (
        <section className="py-20">
          <div className="max-w-md mx-auto px-4">
            <div className="pricing-card text-center" data-testid="login-gate">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-gold/10 border border-gold/20">
                <Lock size={28} className="text-gold" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {t.loginTitle}
              </h2>
              <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>
                {t.loginSubtitle}
              </p>
              <form onSubmit={handleLogin}>
                <label className="block text-left text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {t.passwordLabel}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(false); }}
                  placeholder={t.passwordPlaceholder}
                  className="w-full px-4 py-3 rounded-lg mb-4 text-sm"
                  data-testid="student-code-input"
                />
                {error && (
                  <p className="text-red-400 text-sm mb-4" data-testid="login-error">
                    {t.wrongPassword}
                  </p>
                )}
                <button type="submit" className="btn-gold w-full" data-testid="login-submit" disabled={loading}>
                  {loading ? '...' : t.loginButton}
                </button>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-end mb-6">
              <button onClick={handleLogout} className="flex items-center gap-2 text-sm transition-colors hover:text-gold" style={{ color: 'var(--text-muted)' }} data-testid="logout-btn">
                <LogOut size={14} />
                {t.logout}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {items.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="pricing-card group" data-testid={`material-card-${index}`}>
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6 bg-gold/10 border border-gold/20">
                      <Icon size={28} className="text-gold" />
                    </div>
                    <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{item.title}</h2>
                    <p className="text-gold text-sm font-medium mb-4">{item.subtitle}</p>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
                    <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
                      <span className="font-semibold">{t.topics}:</span> {item.topics}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {[item.language, item.level, item.pages].map((badge, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
                          {i === 0 && <FileText size={12} />}
                          {badge}
                        </span>
                      ))}
                    </div>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn-gold w-full text-center flex items-center justify-center gap-2" data-testid={`download-${index}`}>
                      <Download size={16} />
                      {t.openPdf}
                    </a>
                  </div>
                );
              })}
            </div>
            <div className="mt-12 text-center p-6 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t.info}</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MaterialsPage;
